'use client'
import goToSearchV2 from '@/navigation/CSR/goToSearchV2/goToSearch'
import getLocale from '@/stores/locales'
import type { CollapseProps } from 'antd'
import { Button, Col, Collapse, Row } from 'antd'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { AggregatesValue, CollapsableProps, IParamsByUrl } from '~types/search'
import getActiveAggregates from '../../useCases/getActiveAggregates'
import './CollapseStyles.css'
import Content from './Content'
import Label from './Label'

const Collapsable = ({
  aggregates,
  currencyLocale,
  currencyFormat,
  urlParams,
}: CollapsableProps) => {
  const router = useRouter()
  const copy = getLocale()
  const buttonSize = 'large'
  const selectedAggregates = new Map<string, AggregatesValue>()

  useEffect(() => {
    aggregates.forEach((aggregate) => {
      selectedAggregates.set(aggregate.name, aggregate.value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregates])

  const handleValueListener = (filter: string, value: AggregatesValue) => {
    selectedAggregates.set(filter, value)
  }

  const activeAggregates = getActiveAggregates({
    aggregates,
    currencyLocale,
    currencyFormat,
    valueListener: handleValueListener,
  })

  const items: CollapseProps['items'] = activeAggregates.map((aggregate) => {
    if (aggregate.key === 'availablePromotions') {
      return {
        key: aggregate.key,
        className: 'non-collapsable-item',
        label: (
          <Content
            aggregate={aggregate.contentProps.aggregate}
            value={aggregate.contentProps.value}
            valueListener={aggregate.contentProps.valueListener}
            currencyLocale={aggregate.contentProps.currencyLocale}
            currencyFormat={aggregate.contentProps.currencyFormat}
            agregateLabel={aggregate.label}
          />
        ),
        showArrow: false,
        children: null,
        collapsible: 'disabled',
      }
    }

    return {
      key: aggregate.key,
      label: <Label aggregateName={aggregate.label} />,
      children: (
        <Content
          aggregate={aggregate.contentProps.aggregate}
          value={aggregate.contentProps.value}
          valueListener={aggregate.contentProps.valueListener}
          currencyLocale={aggregate.contentProps.currencyLocale}
          currencyFormat={aggregate.contentProps.currencyFormat}
        />
      ),
    }
  })

  const onClearAll = async () => {
    await goToSearchV2({ router, urlParams, action: 'clearAll' })
  }
  const onApply = async () => {
    const newParams = {
      ...urlParams,
      ...Object.fromEntries(selectedAggregates),
    } as IParamsByUrl
    await goToSearchV2({
      router,
      urlParams: newParams,
      action: 'applyFilters',
    })
  }

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Collapse
            className="search-filters-collapse"
            size="large"
            accordion
            items={items}
            bordered={false}
          />
        </Col>
      </Row>
      <Row justify="space-between" gutter={16}>
        <Col span={12}>
          <Button size={buttonSize} block onClick={onClearAll}>
            {copy?.filter?.clear}
          </Button>
        </Col>
        <Col span={12}>
          <Button type="primary" size={buttonSize} block onClick={onApply}>
            {copy?.filter?.apply}
          </Button>
        </Col>
      </Row>
    </>
  )
}

export default Collapsable
